/**
 * WCAG 2.1 relative luminance and contrast utilities.
 *
 * Used to determine whether light or dark text provides
 * sufficient contrast against a given background color.
 *
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */

const DARK_TEXT = "#11142D";
const LIGHT_TEXT = "#FFFFFF";

/**
 * Converts an sRGB component (0–255) to its linear value
 * per the WCAG 2.1 relative luminance formula.
 */
function linearize(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/**
 * Background luminance where the contrast ratio with LIGHT_TEXT
 * equals the contrast ratio with DARK_TEXT.
 * Derived from: sqrt((L_light + 0.05) * (L_dark + 0.05)) − 0.05
 */
const LUMINANCE_THRESHOLD = (() => {
  const lDark = getRelativeLuminance(DARK_TEXT);
  const lLight = getRelativeLuminance(LIGHT_TEXT);
  return Math.sqrt((lLight + 0.05) * (lDark + 0.05)) - 0.05;
})();

/**
 * Calculates the WCAG 2.1 relative luminance of a hex color.
 * Returns a value between 0 (black) and 1 (white).
 */
export function getRelativeLuminance(hex: string): number {
  const cleaned = hex.replace("#", "");
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);

  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/**
 * Parses a CSS `rgb(r, g, b)` or `rgba(r, g, b, a)` string to a hex color.
 * Returns null if the string cannot be parsed.
 */
export function rgbStringToHex(rgb: string): string | null {
  const match = rgb.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!match) return null;

  const toHex = (n: number): string => n.toString(16).padStart(2, "0");
  return `#${toHex(Number(match[1]))}${toHex(Number(match[2]))}${toHex(
    Number(match[3])
  )}`;
}

/**
 * Returns a text color (dark or light) that provides the highest
 * WCAG contrast ratio against the given background hex color.
 */
export function getContrastTextColor(bgHex: string): string {
  return getRelativeLuminance(bgHex) > LUMINANCE_THRESHOLD
    ? DARK_TEXT
    : LIGHT_TEXT;
}

/**
 * Derives the pre-computed contrast text token from a semantic bg token.
 * e.g. "primary.solid" → "primary.solidContrast"
 *
 * Returns undefined for tokens without a contrast sibling
 * (numbered shades like "gray.600", or "transparent").
 */
export function getContrastToken(bgToken: string): string | undefined {
  if (!bgToken || bgToken === "transparent") return undefined;
  const dotIdx = bgToken.indexOf(".");
  if (dotIdx === -1) return undefined;
  const suffix = bgToken.slice(dotIdx + 1);
  if (/^\d+$/.test(suffix)) return undefined;
  return `${bgToken}Contrast`;
}
